import { maybe, Maybe } from '@passionware/monads';
import { Nullable } from 'v5/platform/typescript';

/**
 * Layer 2 — browsing scope: list API team filters from resolved app scope (URL).
 * Team-level URLs filter by `enterprise_team_id`; org/workspace URLs do not.
 *
 * @see .agents/rules/workspace-scoping-layers.md
 */

export type TeamScoping = { teamId: Nullable<number> };

export type WithTeamScoping<T> = T & TeamScoping;

/** Discriminated app-scope shape (resolved or identity) for team stamps. */
export type TeamScopingSource =
  | { level: 'organization' | 'workspace' }
  | { level: 'team'; teamId: number };

export const teamScopingQueryUtils = {
  ofGlobal: (): TeamScoping => ({ teamId: null }),
  ofTeam: (teamId: number): TeamScoping => ({ teamId }),
  /**
   * Org and workspace URLs are valid: no team filter (`ofGlobal`).
   * Do not throw — list pages convert any browsing scope without a level branch.
   */
  fromScope: (scope: TeamScopingSource): TeamScoping => {
    if (scope.level !== 'team') {
      return teamScopingQueryUtils.ofGlobal();
    }
    return teamScopingQueryUtils.ofTeam(scope.teamId);
  }
};

/** Team id from browsing scope; absent unless the URL is team-scoped. */
export function teamIdFromScope(
  scope: Maybe<TeamScopingSource>
): Maybe<number> {
  return maybe.map(scope, s =>
    s.level === 'team' ? s.teamId : maybe.ofAbsent()
  );
}

export function withTeamScoping<T>(
  searchQuery: T,
  scoping: TeamScoping
): WithTeamScoping<T> {
  return { ...searchQuery, ...scoping };
}
